const reasonToIndication = {
  COPD: "copd",
  HEART_FAILURE: "hearthFailure",
  STROKE: "stroke",
  DIABETES_TYPE_2: "diabetes",
  ASTHMA: "asthma",
  TIA: "tia",
  ISCHAEMIC_HEART_DISEASE: "ihd",
  ATRIAL_FIBRILLATION: "aFib",
};

// Matrix cell states:
//   true       — Mandatory ("Yes")
//   OPTIONAL   — Collected if clinically indicated/available ("Yes*") — shown, NOT required
//   false      — Not collected
// Reflects "Investigations Matrix CDM 4 V1.2 (15-04-2025)" — see
// `.claude/cdm-context/12-investigations-matrix.md` for the authoritative grid.
// The two CDM_4 columns marked "(omit for now)" — Peripheral Arterial Disease and
// CKD Stages 4, 5 & End Stage CKD — are intentionally NOT encoded here.
export const MATRIX_OPTIONAL = "optional";

const getCdmFieldState = (registrationReasons, name, type) => {
  const indications = (registrationReasons || []).map(
    (reason) => reasonToIndication[reason]
  );

  let state = false;

  for (const indication of indications) {
    const cell = cdmMatix[indication]?.[name]?.[type];
    // Mandatory for any one diagnosis ⇒ mandatory overall.
    if (cell === true) return true;
    if (cell === MATRIX_OPTIONAL) state = MATRIX_OPTIONAL;
  }

  return state;
};

// Should the field be shown? (mandatory OR optional for at least one diagnosis)
const getCdmField = (registrationReasons, name, type) => {
  const state = getCdmFieldState(registrationReasons, name, type);

  return state === true || state === MATRIX_OPTIONAL;
};

// Is the field MANDATORY (required in the form)? Optional ("Yes*") cells return false.
const isCdmFieldMandatory = (registrationReasons, name, type) =>
  getCdmFieldState(registrationReasons, name, type) === true;

const M = true;
const O = MATRIX_OPTIONAL;
const NO = false;

// Columns: reg / annual / interim.
export const cdmMatix = {
  diabetes: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: M, annual: O, interim: NO },
    haemoglobin: { reg: M, annual: M, interim: M },
    totalCholesterol: { reg: M, annual: M, interim: O },
    hdlCholesterol: { reg: M, annual: M, interim: O },
    nonHdlCholesterol: { reg: M, annual: M, interim: O },
    ldlCholesterol: { reg: M, annual: M, interim: O },
    triglycerides: { reg: M, annual: M, interim: O },
    hba1c: { reg: M, annual: M, interim: M },
    fastingGlucose: { reg: O, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: M },
    egfr: { reg: M, annual: M, interim: M },
    ntProBNP: { reg: M, annual: NO, interim: NO },
    tftConducted: { reg: M, annual: NO, interim: NO },
    lftConducted: { reg: M, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: M, annual: M, interim: M },
    footAndLimbReview: { reg: M, annual: M, interim: NO },
    retinalScreen: { reg: M, annual: M, interim: M },
    // QRisk3 is "Optional for Interim review" per the active matrix.
    qRisk3: { reg: M, annual: M, interim: O },
  },
  asthma: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    // CDM_4 V1.2 (15-04-2025): asthma Haemoglobin (FBC) at registration is "Yes*"
    // (if clinically indicated/available) = optional — reverts the CDM_3 FBC-mandatory addition.
    haemoglobin: { reg: O, annual: NO, interim: NO },
    totalCholesterol: { reg: NO, annual: NO, interim: NO },
    hdlCholesterol: { reg: NO, annual: NO, interim: NO },
    nonHdlCholesterol: { reg: NO, annual: NO, interim: NO },
    ldlCholesterol: { reg: NO, annual: NO, interim: NO },
    triglycerides: { reg: NO, annual: NO, interim: NO },
    hba1c: { reg: NO, annual: NO, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: NO, annual: NO, interim: NO },
    egfr: { reg: NO, annual: NO, interim: NO },
    ntProBNP: { reg: NO, annual: NO, interim: NO },
    tftConducted: { reg: NO, annual: NO, interim: NO },
    lftConducted: { reg: NO, annual: NO, interim: NO },
    ecg: { reg: O, annual: NO, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: M, annual: O, interim: O },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    // QRisk3 is "Optional for Interim review" per the active matrix.
    qRisk3: { reg: O, annual: O, interim: O },
  },
  copd: {
    copdDyspnoeaScore: { reg: M, annual: M, interim: M },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: NO, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: NO, interim: NO },
    egfr: { reg: M, annual: NO, interim: NO },
    ntProBNP: { reg: NO, annual: NO, interim: NO },
    tftConducted: { reg: M, annual: NO, interim: NO },
    lftConducted: { reg: M, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: M, annual: O, interim: O },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    // QRisk3 is "Optional for Interim review" per the active matrix.
    qRisk3: { reg: M, annual: M, interim: O },
  },
  hearthFailure: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: M, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: NO },
    egfr: { reg: M, annual: M, interim: NO },
    ntProBNP: { reg: M, annual: NO, interim: NO },
    tftConducted: { reg: M, annual: NO, interim: NO },
    lftConducted: { reg: M, annual: NO, interim: NO },
    // Heart Failure is the only condition where ECG is mandatory (not Yes*).
    ecg: { reg: M, annual: M, interim: NO },
    echocardiography: { reg: M, annual: M, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    qRisk3: { reg: M, annual: M, interim: NO },
  },
  aFib: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: M, annual: M, interim: M },
    creatinineClearance: { reg: M, annual: M, interim: M },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: M, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: M },
    egfr: { reg: M, annual: M, interim: M },
    ntProBNP: { reg: M, annual: NO, interim: NO },
    tftConducted: { reg: M, annual: NO, interim: NO },
    lftConducted: { reg: M, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: M, annual: NO, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    qRisk3: { reg: M, annual: M, interim: NO },
  },
  ihd: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: NO, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: NO },
    egfr: { reg: M, annual: M, interim: NO },
    ntProBNP: { reg: M, annual: NO, interim: NO },
    tftConducted: { reg: NO, annual: NO, interim: NO },
    lftConducted: { reg: NO, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    qRisk3: { reg: NO, annual: NO, interim: NO },
  },
  stroke: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: NO, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: NO },
    egfr: { reg: M, annual: M, interim: NO },
    ntProBNP: { reg: NO, annual: NO, interim: NO },
    tftConducted: { reg: NO, annual: NO, interim: NO },
    lftConducted: { reg: NO, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    qRisk3: { reg: NO, annual: NO, interim: NO },
  },
  tia: {
    copdDyspnoeaScore: { reg: NO, annual: NO, interim: NO },
    cha2ds2vascScore: { reg: NO, annual: NO, interim: NO },
    creatinineClearance: { reg: NO, annual: NO, interim: NO },
    acr: { reg: NO, annual: NO, interim: NO },
    haemoglobin: { reg: M, annual: NO, interim: NO },
    totalCholesterol: { reg: M, annual: M, interim: NO },
    hdlCholesterol: { reg: M, annual: M, interim: NO },
    nonHdlCholesterol: { reg: M, annual: M, interim: NO },
    ldlCholesterol: { reg: M, annual: M, interim: NO },
    triglycerides: { reg: M, annual: M, interim: NO },
    hba1c: { reg: M, annual: M, interim: NO },
    fastingGlucose: { reg: NO, annual: NO, interim: NO },
    serumCreatinine: { reg: M, annual: M, interim: NO },
    egfr: { reg: M, annual: M, interim: NO },
    ntProBNP: { reg: NO, annual: NO, interim: NO },
    tftConducted: { reg: NO, annual: NO, interim: NO },
    lftConducted: { reg: NO, annual: NO, interim: NO },
    ecg: { reg: O, annual: O, interim: NO },
    echocardiography: { reg: NO, annual: NO, interim: NO },
    spirometry: { reg: NO, annual: NO, interim: NO },
    amputation: { reg: NO, annual: NO, interim: NO },
    footAndLimbReview: { reg: NO, annual: NO, interim: NO },
    retinalScreen: { reg: NO, annual: NO, interim: NO },
    qRisk3: { reg: NO, annual: NO, interim: NO },
  },
};

export { getCdmField, isCdmFieldMandatory };
